import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Save, MapPin, Clock, Users, DollarSign, Plus, X } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { toursApi } from '../../lib/api'
import RichTextEditor from '../../components/ui/RichTextEditor'

export default function TourEditor() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { language } = useLanguageStore()

    const isEdit = !!id

    const [formData, setFormData] = useState({
        type: 'island',
        price: 0,
        duration: '1 day',
        location: 'Phú Quốc',
        max_participants: 10,
        featured_image: '',
        status: 'active',
        title_en: '',
        title_vi: '',
        description_en: '',
        description_vi: '',
    })

    // Highlights and Includes as arrays for +/- UI
    const [highlightsEn, setHighlightsEn] = useState<string[]>([''])
    const [highlightsVi, setHighlightsVi] = useState<string[]>([''])
    const [includesEn, setIncludesEn] = useState<string[]>([''])
    const [includesVi, setIncludesVi] = useState<string[]>([''])

    const [activeTab, setActiveTab] = useState<'en' | 'vi'>('vi')
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

    useEffect(() => {
        if (isEdit && id) {
            setLoading(true)
            toursApi.getById(parseInt(id))
                .then((response) => {
                    const data = response.data?.data
                    if (data) {
                        setFormData({
                            type: data.type || 'island',
                            price: data.price || 0,
                            duration: data.duration || '1 day',
                            location: data.location || 'Phú Quốc',
                            max_participants: data.max_participants || 10,
                            featured_image: data.featured_image || '',
                            status: data.status || 'active',
                            title_en: data.title_en || '',
                            title_vi: data.title_vi || '',
                            description_en: data.description_en || '',
                            description_vi: data.description_vi || '',
                        })

                        // Parse pipe-separated highlights
                        const hlEn = data.highlights_en ? data.highlights_en.split('|').map((s: string) => s.trim()) : ['']
                        const hlVi = data.highlights_vi ? data.highlights_vi.split('|').map((s: string) => s.trim()) : ['']
                        setHighlightsEn(hlEn.length > 0 && hlEn[0] ? hlEn : [''])
                        setHighlightsVi(hlVi.length > 0 && hlVi[0] ? hlVi : [''])

                        // Parse pipe-separated includes
                        const incEn = data.includes_en ? data.includes_en.split('|').map((s: string) => s.trim()) : ['']
                        const incVi = data.includes_vi ? data.includes_vi.split('|').map((s: string) => s.trim()) : ['']
                        setIncludesEn(incEn.length > 0 && incEn[0] ? incEn : [''])
                        setIncludesVi(incVi.length > 0 && incVi[0] ? incVi : [''])
                    }
                })
                .catch((err) => console.error('Failed to fetch tour:', err))
                .finally(() => setLoading(false))
        }
    }, [id, isEdit])

    const handleSave = async () => {
        setSaving(true)
        setMessage(null)

        // Convert arrays to pipe-separated strings
        const dataToSend = {
            ...formData,
            highlights_en: highlightsEn.filter(h => h.trim()).join('|'),
            highlights_vi: highlightsVi.filter(h => h.trim()).join('|'),
            includes_en: includesEn.filter(i => i.trim()).join('|'),
            includes_vi: includesVi.filter(i => i.trim()).join('|'),
        }

        try {
            if (isEdit && id) {
                const response = await toursApi.update(parseInt(id), dataToSend)
                if (response.error) throw new Error(response.error)
                setMessage({ type: 'success', text: language === 'en' ? 'Tour saved!' : 'Tour đã được lưu!' })
            } else {
                const response = await toursApi.create(dataToSend)
                if (response.error) throw new Error(response.error)
                setMessage({ type: 'success', text: language === 'en' ? 'Tour created!' : 'Tour đã được tạo!' })
                if (response.data?.id) {
                    navigate(`/admin/tours/${response.data.id}/edit`)
                }
            }
        } catch (error) {
            console.error('Failed to save:', error)
            setMessage({ type: 'error', text: language === 'en' ? 'Failed to save' : 'Lưu thất bại' })
        } finally {
            setSaving(false)
        }
    }

    // List helpers for highlights
    const addHighlight = (lang: 'en' | 'vi') => {
        if (lang === 'en') setHighlightsEn([...highlightsEn, ''])
        else setHighlightsVi([...highlightsVi, ''])
    }
    const removeHighlight = (lang: 'en' | 'vi', index: number) => {
        if (lang === 'en') setHighlightsEn(highlightsEn.filter((_, i) => i !== index))
        else setHighlightsVi(highlightsVi.filter((_, i) => i !== index))
    }
    const updateHighlight = (lang: 'en' | 'vi', index: number, value: string) => {
        if (lang === 'en') {
            const updated = [...highlightsEn]; updated[index] = value; setHighlightsEn(updated)
        } else {
            const updated = [...highlightsVi]; updated[index] = value; setHighlightsVi(updated)
        }
    }

    // List helpers for includes
    const addInclude = (lang: 'en' | 'vi') => {
        if (lang === 'en') setIncludesEn([...includesEn, ''])
        else setIncludesVi([...includesVi, ''])
    }
    const removeInclude = (lang: 'en' | 'vi', index: number) => {
        if (lang === 'en') setIncludesEn(includesEn.filter((_, i) => i !== index))
        else setIncludesVi(includesVi.filter((_, i) => i !== index))
    }
    const updateInclude = (lang: 'en' | 'vi', index: number, value: string) => {
        if (lang === 'en') {
            const updated = [...includesEn]; updated[index] = value; setIncludesEn(updated)
        } else {
            const updated = [...includesVi]; updated[index] = value; setIncludesVi(updated)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    const currentHighlights = activeTab === 'en' ? highlightsEn : highlightsVi
    const currentIncludes = activeTab === 'en' ? includesEn : includesVi

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/tours')} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEdit
                                ? (language === 'en' ? 'Edit Tour' : 'Sửa Tour')
                                : (language === 'en' ? 'New Tour' : 'Thêm Tour Mới')
                            }
                        </h1>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {language === 'en' ? 'Save' : 'Lưu'}
                </button>
            </div>

            {message && (
                <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Language Tabs */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setActiveTab('vi')}
                                className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'vi' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                🇻🇳 Tiếng Việt
                            </button>
                            <button
                                onClick={() => setActiveTab('en')}
                                className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'en' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                🇬🇧 English
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {activeTab === 'en' ? 'Title (English)' : 'Tiêu đề (Tiếng Việt)'}
                                </label>
                                <input
                                    type="text"
                                    value={activeTab === 'en' ? formData.title_en : formData.title_vi}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        [activeTab === 'en' ? 'title_en' : 'title_vi']: e.target.value
                                    })}
                                    placeholder={activeTab === 'en' ? 'Tour title...' : 'Tiêu đề tour...'}
                                    className="w-full px-4 py-3 text-lg border rounded-lg"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Description - WYSIWYG */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">
                            {activeTab === 'en' ? 'Description (English)' : 'Mô tả chi tiết (Tiếng Việt)'}
                        </h3>
                        <RichTextEditor
                            key={`description-${activeTab}`}
                            content={activeTab === 'en' ? formData.description_en : formData.description_vi}
                            onChange={(content: string) => setFormData({
                                ...formData,
                                [activeTab === 'en' ? 'description_en' : 'description_vi']: content
                            })}
                            placeholder={activeTab === 'en' ? 'Write detailed tour description for SEO...' : 'Viết mô tả chi tiết tour để SEO tốt hơn...'}
                        />
                    </div>

                    {/* Highlights - +/- textareas */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">
                            {activeTab === 'en' ? 'Highlights (English)' : 'Điểm nổi bật (Tiếng Việt)'}
                        </h3>
                        <div className="space-y-2">
                            {currentHighlights.map((item, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => updateHighlight(activeTab, index, e.target.value)}
                                        placeholder={activeTab === 'en' ? `Highlight ${index + 1}...` : `Điểm nổi bật ${index + 1}...`}
                                        className="flex-1 px-3 py-2 border rounded-lg"
                                    />
                                    <button
                                        onClick={() => removeHighlight(activeTab, index)}
                                        disabled={currentHighlights.length <= 1}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={() => addHighlight(activeTab)}
                                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                            >
                                <Plus className="w-4 h-4" />
                                {activeTab === 'en' ? 'Add Highlight' : 'Thêm điểm nổi bật'}
                            </button>
                        </div>
                    </div>

                    {/* Includes - +/- textareas */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">
                            {activeTab === 'en' ? 'Includes (English)' : 'Bao gồm (Tiếng Việt)'}
                        </h3>
                        <div className="space-y-2">
                            {currentIncludes.map((item, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => updateInclude(activeTab, index, e.target.value)}
                                        placeholder={activeTab === 'en' ? `Include ${index + 1}...` : `Bao gồm ${index + 1}...`}
                                        className="flex-1 px-3 py-2 border rounded-lg"
                                    />
                                    <button
                                        onClick={() => removeInclude(activeTab, index)}
                                        disabled={currentIncludes.length <= 1}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={() => addInclude(activeTab)}
                                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                            >
                                <Plus className="w-4 h-4" />
                                {activeTab === 'en' ? 'Add Include' : 'Thêm mục bao gồm'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Type */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Type' : 'Loại'}</h3>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="island">{language === 'en' ? 'Island Tour' : 'Tour Đảo'}</option>
                            <option value="snorkeling">{language === 'en' ? 'Snorkeling' : 'Lặn ngắm san hô'}</option>
                            <option value="fishing">{language === 'en' ? 'Fishing' : 'Câu cá'}</option>
                            <option value="sunset">{language === 'en' ? 'Sunset Tour' : 'Tour hoàng hôn'}</option>
                            <option value="city">{language === 'en' ? 'City Tour' : 'Tour thành phố'}</option>
                            <option value="adventure">{language === 'en' ? 'Adventure' : 'Phiêu lưu'}</option>
                            <option value="food">{language === 'en' ? 'Food Tour' : 'Tour ẩm thực'}</option>
                            <option value="ticket">{language === 'en' ? '🎟️ Ticket (Simple)' : '🎟️ Vé (Đơn giản)'}</option>
                        </select>
                    </div>

                    {/* Status */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Status' : 'Trạng thái'}</h3>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="active">{language === 'en' ? 'Active' : 'Hoạt động'}</option>
                            <option value="draft">{language === 'en' ? 'Draft' : 'Nháp'}</option>
                        </select>
                    </div>

                    {/* Price */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            {language === 'en' ? 'Price (VND)' : 'Giá (VND)'}
                        </h3>
                        <input
                            type="number"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    {/* Duration */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {language === 'en' ? 'Duration' : 'Thời gian'}
                        </h3>
                        <input
                            type="text"
                            value={formData.duration}
                            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                            placeholder="1 ngày, 2 giờ..."
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    {/* Max Participants */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {language === 'en' ? 'Max Participants' : 'Số người tối đa'}
                        </h3>
                        <input
                            type="number"
                            value={formData.max_participants}
                            onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 10 })}
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    {/* Location */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {language === 'en' ? 'Location' : 'Địa điểm'}
                        </h3>
                        <input
                            type="text"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            placeholder="Phú Quốc..."
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    {/* Featured Image */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Featured Image URL' : 'URL ảnh đại diện'}</h3>
                        <input
                            type="text"
                            value={formData.featured_image}
                            onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                            placeholder="https://..."
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                        {formData.featured_image && (
                            <img src={formData.featured_image} alt="Preview" className="w-full h-32 object-cover rounded-lg mt-2" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
